// ABOUTME: Scratchpad state with a debounced, encrypted write-behind to Firestore.
// ABOUTME: localStorage is the durable buffer; a dirty flag guards the debounce window.

import { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { scratchpadService } from "../services/firebase";
import { cryptoService } from "../services/crypto";

// Keys are per user. A single shared key meant the previous account's note
// flashed on screen for whoever signed in next on the same browser.
function cacheKey(userId: string): string {
  return `scratchpad_cache_${userId}`;
}
// Set while an edit has not yet reached Firestore. Without it, closing the tab
// inside the debounce window would leave newer local text that the next load
// would silently overwrite with the older remote copy.
function dirtyKey(userId: string): string {
  return `scratchpad_dirty_${userId}`;
}

// The pre-per-user keys. Migrated once, below.
const LEGACY_CACHE_KEY = "scratchpad_cache";
const LEGACY_DIRTY_KEY = "scratchpad_dirty";

const DEBOUNCE_MS = 800;

export type SaveState = "idle" | "pending" | "saving" | "saved" | "error";

function getCachedText(userId: string): string {
  try {
    return localStorage.getItem(cacheKey(userId)) || "";
  } catch {
    return "";
  }
}

function setCachedText(userId: string, text: string): void {
  try {
    localStorage.setItem(cacheKey(userId), text);
  } catch {
    // localStorage may be full or unavailable
  }
}

function clearCachedText(userId: string): void {
  try {
    localStorage.removeItem(cacheKey(userId));
    localStorage.removeItem(dirtyKey(userId));
  } catch {
    // Silently ignore
  }
}

function setDirty(userId: string, dirty: boolean): void {
  try {
    if (dirty) localStorage.setItem(dirtyKey(userId), "1");
    else localStorage.removeItem(dirtyKey(userId));
  } catch {
    // Silently ignore
  }
}

function isDirty(userId: string): boolean {
  try {
    return localStorage.getItem(dirtyKey(userId)) === "1";
  } catch {
    return false;
  }
}

/**
 * Moves the old shared cache onto this user's keys, once. Only an unsaved
 * (dirty) edit is worth carrying over — it has not reached Firestore and would
 * otherwise be lost. A clean copy is already on the server, and we cannot know
 * which account it belonged to, so it is simply dropped.
 */
function migrateLegacyCache(userId: string): void {
  try {
    const legacyText = localStorage.getItem(LEGACY_CACHE_KEY);
    if (legacyText === null) return;
    const legacyDirty = localStorage.getItem(LEGACY_DIRTY_KEY) === "1";
    if (legacyDirty && !isDirty(userId)) {
      localStorage.setItem(cacheKey(userId), legacyText);
      localStorage.setItem(dirtyKey(userId), "1");
    }
    localStorage.removeItem(LEGACY_CACHE_KEY);
    localStorage.removeItem(LEGACY_DIRTY_KEY);
  } catch {
    // Silently ignore
  }
}

export function useScratchpad(userId: string | undefined) {
  const [text, setText] = useState(() => {
    if (!userId) return "";
    migrateLegacyCache(userId);
    return getCachedText(userId);
  });
  const [loading, setLoading] = useState(!!userId);
  /** Load/decrypt failures — these justify replacing the editor with an error. */
  const [error, setError] = useState<Error | null>(null);
  /** Save failures — surfaced inline; the note itself stays on screen. */
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");

  const pendingRef = useRef<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userIdRef = useRef(userId);
  useLayoutEffect(() => {
    userIdRef.current = userId;
  });

  /** Writes any pending edit immediately. Never throws — callers are fire-and-forget. */
  const flush = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    const uid = userIdRef.current;
    if (pending === null || !uid) return;
    pendingRef.current = null;

    setSaveState("saving");
    try {
      const encrypted = cryptoService.encrypt(pending);
      await scratchpadService.updateScratchpad(uid, encrypted);
      // Only clear the dirty flag once the write has actually landed.
      setDirty(uid, false);
      setSaveError(null);
      setSaveState("saved");
    } catch (err) {
      // Keep the text cached and still marked dirty so it survives a reload and
      // gets re-sent. Deliberately does not throw: this runs unawaited from an
      // onChange handler, where a rejection would be an unhandled rejection per
      // keystroke.
      pendingRef.current = pending;
      setSaveError(err instanceof Error ? err : new Error("Failed to save scratchpad"));
      setSaveState("error");
    }
  }, []);

  // A different user (or none) starts from their own cached note, never the
  // previous user's. Reset while rendering so no frame shows the old note.
  const [loadedUserId, setLoadedUserId] = useState(userId);
  if (userId !== loadedUserId) {
    setLoadedUserId(userId);
    setText(userId ? getCachedText(userId) : "");
    setLoading(!!userId);
    setError(null);
  }

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    migrateLegacyCache(userId);

    const loadScratchpad = async () => {
      try {
        const data = await scratchpadService.getScratchpad(userId);
        if (cancelled) return;
        if (data) {
          const decryptedText = cryptoService.decrypt(data.text);
          const cached = getCachedText(userId);

          if (isDirty(userId) && cached !== decryptedText) {
            // A previous session had unsaved edits. The local copy is newer by
            // construction, so keep it and push it up rather than clobbering it.
            setText(cached);
            pendingRef.current = cached;
            setSaveState("pending");
            void flush();
          } else {
            setText(decryptedText);
            setCachedText(userId, decryptedText);
            setDirty(userId, false);
          }
        } else if (isDirty(userId)) {
          // Nothing on the server yet but an unsaved local edit: send it.
          const cached = getCachedText(userId);
          setText(cached);
          pendingRef.current = cached;
          setSaveState("pending");
          void flush();
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error("Failed to load scratchpad"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadScratchpad();
    return () => {
      cancelled = true;
    };
  }, [userId, flush]);

  // Push any pending edit out when the widget unmounts (column change, sign-out,
  // navigation) rather than dropping it.
  useEffect(() => {
    return () => {
      void flush();
    };
  }, [flush]);

  const updateText = useCallback(
    (newText: string) => {
      const uid = userIdRef.current;
      // Local state and cache update immediately — the editor stays responsive
      // and the text is durable even before it reaches Firestore.
      setText(newText);
      if (uid) {
        setCachedText(uid, newText);
        setDirty(uid, true);
      }
      pendingRef.current = newText;
      setSaveState("pending");

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void flush();
      }, DEBOUNCE_MS);
    },
    [flush]
  );

  const clearText = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
    try {
      if (!userId) throw new Error("User not authenticated");
      setSaveError(null);
      await scratchpadService.clearScratchpad(userId);
      setText("");
      clearCachedText(userId);
      setSaveState("idle");
    } catch (err) {
      const wrapped = err instanceof Error ? err : new Error("Failed to clear scratchpad");
      setError(wrapped);
      throw wrapped;
    }
  }, [userId]);

  return {
    text,
    loading,
    error,
    saveError,
    saveState,
    updateText,
    clearText,
    /** Force an immediate write — used on blur. */
    flush,
  };
}
